import type { ActivityStep, AIConversationMessageDto } from '@/types/aiChat'

/**
 * Turning a session's message array into the activity behind one answer.
 *
 * The API persists a tool step as two rows — an assistant row holding `FunctionCall` plus that
 * call's tokens, model and cost, then a `function` row whose `Content` is the serialized result.
 * Both have always been on the wire and both were discarded by the dashboard. These helpers pair
 * them back up so one call renders as one row.
 *
 * Pure and dependency-free on purpose: this is the whole of the derivation, and it is the part
 * worth testing.
 */

/** Where the turn being rendered begins: one past the last `user` row. Returns 0 when there is
 *  no user row at all, which is the opening greeting. */
export function turnStartIndex(messages: AIConversationMessageDto[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].Role === 'user') return i + 1
  }
  return 0
}

/**
 * Collect the tool calls made from `fromIdx` onward, pairing each with the row carrying its
 * result.
 *
 * A call with no matching result still yields a row: the turn ended before the tool returned,
 * and hiding that would be the same kind of lie the phrase rotator told. Results are matched by
 * tool-call id rather than by position, because a response may carry more than one call and the
 * order results come back in is the provider's business, not ours.
 */
export function buildActivitySteps(
  messages: AIConversationMessageDto[],
  fromIdx: number
): ActivityStep[] {
  const start = Math.max(0, fromIdx)

  // The result row carries the tool's own outcome and duration; the call row's belong to the
  // model call that asked for it. Keeping them apart is the difference between "the search took
  // 3s" and "deciding to search took 3s".
  const resultsByCallId = new Map<
    string,
    { content: string; outcome: string | null; durationMs: number | null }
  >()
  for (let i = start; i < messages.length; i++) {
    const message = messages[i]
    const functionResult = message.FunctionResult
    if (!functionResult) continue
    resultsByCallId.set(functionResult.Id, {
      content: message.Content,
      outcome: message.Outcome ?? null,
      durationMs: message.DurationMs ?? null,
    })
  }

  const steps: ActivityStep[] = []
  for (let i = start; i < messages.length; i++) {
    const message = messages[i]
    // One assistant message can hold several calls: the model asks for them together and the API
    // records them together. The singular `FunctionCall` is the deprecated wire alias for the
    // first entry, so reading the list is both the complete answer and the current one.
    const calls = message.FunctionCalls
    if (!calls?.length) continue

    // Tokens, model and cost belong to the ONE model call that asked for this whole batch, not to
    // any single tool in it. Copying them onto every row would have three rows each claiming the
    // full cost, and the rows would sum to three times what the session meter says for that turn.
    // They go on the first row only, marked as covering the batch.
    calls.forEach((call, indexInBatch) => {
      const resolved = resultsByCallId.get(call.Id)
      const ownsBatchCost = indexInBatch === 0
      steps.push({
        id: call.Id,
        name: call.Name,
        arguments: call.Arguments,
        result: resolved?.content ?? null,
        outcome: resolved?.outcome ?? null,
        durationMs: resolved?.durationMs ?? null,
        inputTokens: ownsBatchCost ? message.InputTokens ?? null : null,
        outputTokens: ownsBatchCost ? message.OutputTokens ?? null : null,
        modelId: ownsBatchCost ? message.ModelId ?? null : null,
        costUsd: ownsBatchCost ? message.CostUsd ?? null : null,
        /** How many tool calls the one model call this cost covers asked for. 1 means the figures
         *  describe this row alone. */
        batchSize: calls.length,
      })
    })
  }
  return steps
}
